import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { userPreferencesService } from '../../services/userPreferencesService';
import { UserPreferences } from '../../types/userPreferences';

interface BCPAssessmentProps {
  onComplete: (assessment: BCPAssessmentData) => void;
  onSkip: () => void;
  isFirstBCP: boolean;
  existingPreferences?: UserPreferences | null;
}

export interface BCPAssessmentData {
  bcp_purpose: 'internal' | 'compliance' | 'both';
  flash_rtc_interest: boolean;
  cyber_assessment_interest: boolean;
  lms_interest: boolean;
  tabletop_exercise_interest: boolean;
}

interface PackageRecommendation {
  recommended_package: 'essential' | 'gold' | 'premium';
  package_description: string;
  included_services: string[];
  next_steps: string[];
}

const BCPAssessment: React.FC<BCPAssessmentProps> = ({ onComplete, onSkip, isFirstBCP, existingPreferences }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Initialize with existing preferences if available, otherwise use defaults
  const getInitialAssessment = (): BCPAssessmentData => {
    if (existingPreferences) {
      return {
        bcp_purpose: existingPreferences.bcp_purpose ?? 'internal',
        flash_rtc_interest: existingPreferences.flash_rtc_interest ?? false,
        cyber_assessment_interest: existingPreferences.cyber_assessment_interest ?? false,
        lms_interest: existingPreferences.lms_interest ?? false,
        tabletop_exercise_interest: existingPreferences.tabletop_exercise_interest ?? false,
      };
    }
    return {
      bcp_purpose: 'internal',
      flash_rtc_interest: false,
      cyber_assessment_interest: false,
      lms_interest: false,
      tabletop_exercise_interest: false,
    };
  };

  const [assessment, setAssessment] = useState<BCPAssessmentData>(getInitialAssessment());
  const [recommendation, setRecommendation] = useState<PackageRecommendation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug: Log existing preferences
  React.useEffect(() => {
    if (existingPreferences) {
      console.log('🔍 [BCP ASSESSMENT] Existing preferences loaded:', existingPreferences);
      console.log('🔍 [BCP ASSESSMENT] Initial assessment values:', getInitialAssessment());
    }
  }, [existingPreferences]);

  const questions = [
    {
      id: 'bcp_purpose',
      question: 'Is this ISO 22301:2019 Business Continuity (BC) plan intended for internal use only to identify risks and to protect people and operations, or is it to meet regulatory, contractual, or insurance requirements, or both?',
      type: 'radio',
      options: [
        { value: 'internal', label: 'INTERNAL USE ONLY', description: 'For internal risk management and mitigation strategies' },
        { value: 'compliance', label: 'REGULATORY, CONTRACTUAL, INSURANCE', description: 'Needed to meet regulatory, contractual, or insurance requirements' },
        { value: 'both', label: 'BOTH', description: 'Both internal use and compliance requirements' }
      ]
    },
    {
      id: 'flash_rtc_interest',
      question: 'Are you interested in a crisis communication plan and a subscription to our crisis communication app called Flash RTC to be used as backup for crisis communications should you have a network disruption or outage?',
      type: 'boolean',
      description: 'The Flash RTC app is designed for both Android and iOS phones to provide reliable, secure communication during critical events when normal networks may be down. For more information, please visit www.flash-em.com'
    },
    {
      id: 'cyber_assessment_interest',
      question: 'Are you interested in a cyber security assessment to determine cyber risks and mitigation strategies?',
      type: 'boolean',
      description: 'This Assessment will identify your cybersecurity posture and offer recommendations and mitigation strategies for security improvement.'
    },
    {
      id: 'lms_interest',
      question: 'Are you interested in training your workforce in corporate risk management and crisis response via our on-line learning management academy?',
      type: 'boolean',
      description: 'For more information, please visit www.flash-em.com'
    },
    {
      id: 'tabletop_exercise_interest',
      question: 'Are you interested in conducting virtual tabletop exercises with your workforce to practice risk identification and response procedures?',
      type: 'boolean',
      description: 'For more information, please visit www.flash-em.com'
    }
  ];

  const handleAnswer = (questionId: keyof BCPAssessmentData, value: string | boolean) => {
    setAssessment(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Submit assessment and get recommendation
      submitAssessment();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const submitAssessment = async () => {
    setIsSubmitting(true);
    try {
      const recommendationData = await userPreferencesService.submitAssessment(assessment);
      setRecommendation(recommendationData);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      // Continue anyway
      onComplete(assessment);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptRecommendation = () => {
    onComplete(assessment);
  };

  const getPackageColor = (packageType: string) => {
    switch (packageType) {
      case 'essential': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'gold': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'premium': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPackageIcon = (packageType: string) => {
    switch (packageType) {
      case 'essential': return '📋';
      case 'gold': return '🥇';
      case 'premium': return '💎';
      default: return '📄';
    }
  };

  if (recommendation) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Your Recommended Package</h1>
          <p className="text-lg text-gray-600">Based on your responses, we recommend the following package for your business continuity needs.</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg border-2 border-gray-200 p-8 mb-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">{getPackageIcon(recommendation.recommended_package)}</div>
            <h2 className={`text-2xl font-bold px-4 py-2 rounded-lg border-2 inline-block ${getPackageColor(recommendation.recommended_package)}`}>
              {recommendation.recommended_package.charAt(0).toUpperCase() + recommendation.recommended_package.slice(1)} Package
            </h2>
            <p className="text-gray-600 mt-2">{recommendation.package_description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Included Services</h3>
              <ul className="space-y-2">
                {recommendation.included_services.map((service, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Next Steps</h3>
              <ol className="space-y-2">
                {recommendation.next_steps.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-semibold mr-3">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={handleAcceptRecommendation}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Accept Recommendation & Continue
          </button>
          <button
            onClick={onSkip}
            className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
          >
            Skip & Continue
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentStep];
  const isAnswered = assessment[currentQuestion.id as keyof BCPAssessmentData] !== undefined;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {isFirstBCP ? 'Why a BCMS Matters for Your Business' : 'Update Your Preferences'}
        </h1>
        {isFirstBCP ? (
          <div className="text-left max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 mb-4">
              A Business Continuity Management System (BCMS) is more than a plan—it's a safeguard for your people, your operations, and your reputation. Disruptions like cyberattacks, supply chain breakdowns, or natural disasters can strike without warning, and for many small and mid-sized businesses, even a short outage can threaten survival.
            </p>
            <p className="text-lg text-gray-700 mb-4">
              By preparing your workforce, identifying risks, and building mitigation strategies, you're not just checking a compliance box, you're making a smart leadership decision. A BCMS ensures your team knows how to respond, your operations recover faster, and your customers and partners see you as resilient and trustworthy.
            </p>
            <p className="text-lg text-gray-700 mb-6">
              Choosing Flash AI's plans means choosing continuity in a world where disruption is unavoidable. A generated plan is not a certification.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-lg font-semibold text-green-800 text-center">Congratulations!</p>
            </div>
          </div>
        ) : (
          <p className="text-lg text-gray-600 mb-6">
            Let us know if your preferences have changed since your last assessment. This will help us provide updated package recommendations.
          </p>
        )}

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
          />
        </div>
        <p className="text-sm text-gray-500">Question {currentStep + 1} of {questions.length}</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {currentQuestion.question}
        </h2>

        {currentQuestion.description && (
          <p className="text-gray-600 mb-6">{currentQuestion.description}</p>
        )}

        <div className="space-y-4">
          {currentQuestion.type === 'radio' ? (
            currentQuestion.options?.map((option) => (
              <label
                key={option.value}
                className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  assessment[currentQuestion.id as keyof BCPAssessmentData] === option.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name={currentQuestion.id}
                  value={option.value}
                  checked={assessment[currentQuestion.id as keyof BCPAssessmentData] === option.value}
                  onChange={(e) => handleAnswer(currentQuestion.id as keyof BCPAssessmentData, e.target.value)}
                  className="mt-1 mr-3"
                />
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-600">{option.description}</div>
                </div>
              </label>
            ))
          ) : (
            <div className="flex space-x-4">
              <button
                onClick={() => handleAnswer(currentQuestion.id as keyof BCPAssessmentData, true)}
                className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg transition-colors ${
                  assessment[currentQuestion.id as keyof BCPAssessmentData] === true
                    ? 'border-green-500 bg-green-50 text-green-800'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <CheckCircleIcon className="w-6 h-6 mr-2" />
                Yes
              </button>
              <button
                onClick={() => handleAnswer(currentQuestion.id as keyof BCPAssessmentData, false)}
                className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg transition-colors ${
                  assessment[currentQuestion.id as keyof BCPAssessmentData] === false
                    ? 'border-red-500 bg-red-50 text-red-800'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <XCircleIcon className="w-6 h-6 mr-2" />
                No
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-8">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex space-x-4">
            <button
              onClick={onSkip}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Skip Assessment
            </button>
            <button
              onClick={handleNext}
              disabled={!isAnswered || isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Processing...' : currentStep === questions.length - 1 ? 'Get Recommendation' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BCPAssessment;
